import config from './config.json';

export type GlobalRoutes = {
  // Infrastructure Services (APIs, WebSockets, etc.)
  API_BASE_URL: string;
  METRICS_API_BASE_URL: string;
  REALTIME_API_BASE_URL: string;
  REALTIME_API_WEBSOCKET_URL: string;

  // Website URLs (User-facing sites)
  PAVILION_PLAYER_BASE_URL: string;
  PRIME_PLAYER_BASE_URL: string;
  DASHBOARD_BASE_URL: string;
  MARKETING_BASE_URL: string;
  DEMO_BASE_URL: string;
  BEACON_BASE_URL: string;
  ADMIN_DASHBOARD_BASE_URL: string;

  //Analytics API URLs
  ANALYTICS_API_URL: string;

  // Download URLs
  PLAYCAST_DOWNLOAD_URL: string;
  PAVILION_DOWNLOAD_URL: string;
}

const ensureTrailingSlash = (url: string) => {
  if (url.endsWith('/')) return url;
  return url.concat('/');
}

// Infrastructure Services Configuration
// These use gibber.com and pogcast.lol domains as currently configured
type InfraServiceConfig = {
  localPort: number;
  releaseSubdomain: string;
  devSubdomain: string;
  suffix?: string;
  websocket?: boolean;
}

const INFRA_SERVICES = {
  API: {
    localPort: 3030,
    releaseSubdomain: 'api',
    devSubdomain: 'api',
  },
  METRICS_API: {
    localPort: 3031,
    releaseSubdomain: 'metrics',
    devSubdomain: 'metrics',
  },
  REALTIME_API: {
    localPort: 3000,
    releaseSubdomain: 'realtime',
    devSubdomain: 'realtime',
  },
  REALTIME_API_WEBSOCKET: {
    localPort: 3210,
    releaseSubdomain: 'realtime',
    devSubdomain: 'realtime',
    suffix: 'ws',
    websocket: true,
  },
} satisfies Record<string, InfraServiceConfig>

// Website Configuration
// These always use playcast.io domain for consistency
type WebsiteConfig = {
  localPort: number;
  releaseSubdomain: string;
  subdomain: string;
}

const WEBSITES = {
  MARKETING: {
    localPort: 4204,
    releaseSubdomain: '',
    subdomain: 'marketing',
  },
  PRIME_PLAYER: {
    localPort: 4204,
    releaseSubdomain: '',
    subdomain: 'marketing',
  },
  PAVILION_PLAYER: {
    localPort: 4204,
    releaseSubdomain: 'pavilion',
    subdomain: 'pavilion',
  },
  DEMO: {
    localPort: 4206,
    releaseSubdomain: 'demo',
    subdomain: 'demo',
  },
  BEACON: {
    localPort: 4206,
    releaseSubdomain: 'demo',
    subdomain: 'demo',
  },
  DASHBOARD: {
    localPort: 4200,
    releaseSubdomain: 'dashboard',
    subdomain: 'dashboard',
  },
  ADMIN_DASHBOARD: {
    localPort: 4400,
    releaseSubdomain: 'admin-dashboard',
    subdomain: 'admin-dashboard',
  },
} satisfies Record<string, WebsiteConfig>

// Download URLs Configuration
// These use s3.amazonaws.com domain for consistency

type DownloadUrlConfig = {
  bucketName: string;
  nameTemplate: string;
}

const DOWNLOAD_URLS = {
  PLAYCAST_DOWNLOAD_URL: {
    bucketName: 'playcast-build-artifacts',
    nameTemplate: 'Playcast-Bootstrapper-${version}.exe',
  },
  PAVILION_DOWNLOAD_URL: {
    bucketName: 'playcast-build-artifacts',
    nameTemplate: 'Pavilion-Installer-${version}.exe',
  },
} satisfies Record<string, DownloadUrlConfig>

// Analytics Configuration
// Uses AWS API Gateway endpoints
type AnalyticsConfig = {
  devUrl: string;
  prodUrl: string;
}

const ANALYTICS = {
  PLAYCAST_ANALYTICS: {
    devUrl: 'https://c829ww805i.execute-api.us-east-1.amazonaws.com/dev/data',
    prodUrl: 'https://iut5cqb7cf.execute-api.us-east-1.amazonaws.com/prod/data',
  },
} satisfies Record<string, AnalyticsConfig>

const generateInfraServiceUrl = (serviceName: keyof typeof INFRA_SERVICES, branchName: string): string => {
  const service = INFRA_SERVICES[serviceName];
  if (!service) throw new Error(`Unknown infrastructure service: ${serviceName}`);

  const isLocal = branchName === 'local';
  const isRelease = branchName === 'release';
  const isPrerelease = branchName === 'prerelease';

  let url = '';

  const wsPath = ('suffix' in service && service.suffix) ? `/${service.suffix}` : '';
  const protocol = ('websocket' in service && service.websocket) ? 'wss:' : 'https:';
  if (isLocal) {
    const protocol = ('websocket' in service && service.websocket) ? 'ws:' : 'http:';
    url = `${protocol}//localhost:${service.localPort}${wsPath}`;
  } else if (isRelease) {
    url = `${protocol}//${service.releaseSubdomain}.gibber.com${wsPath}`;
  } else if (isPrerelease) {
    url = `${protocol}//prerelease.${service.releaseSubdomain}.gibber.com${wsPath}`;
  } else {
    url = `${protocol}//${branchName}.${service.devSubdomain}.pogcast.lol${wsPath}`;
  }
  if (!('websocket' in service) || !service.websocket) {
    url = ensureTrailingSlash(url);
  }
  return url;
}

const ANALYTICS_URLS = {
  releaseUrl: 'https://iut5cqb7cf.execute-api.us-east-1.amazonaws.com/prod/data',
  devUrl: 'https://c829ww805i.execute-api.us-east-1.amazonaws.com/dev/data',
}

const generateAnalyticsUrl = (analyticsName: keyof typeof ANALYTICS, branchName: string): string => {
  if (branchName === 'local') {
    return ANALYTICS_URLS.devUrl;
  } else if (branchName === 'release') {
    return ANALYTICS_URLS.releaseUrl;
  } else {
    return ANALYTICS_URLS.devUrl;
  }
}

const generateWebsiteUrl = (websiteName: keyof typeof WEBSITES, branchName: string): string => {
  const website = WEBSITES[websiteName];
  if (!website) throw new Error(`Unknown website: ${websiteName}`);

  const isLocal = branchName === 'local';
  const isRelease = branchName === 'release';

  if (isLocal) {
    return ensureTrailingSlash(`http://localhost:${website.localPort}`);
  } else if (isRelease) {
    // Production websites use playcast.io
    const subdomain = website.releaseSubdomain ?? website.subdomain;
    const domain = subdomain ? `${subdomain}.playcast.io` : 'playcast.io';
    return ensureTrailingSlash(`https://${domain}`);
  } else {
    // Development websites also use playcast.io with branch prefix
    const domain = website.subdomain ? `${branchName}.${website.subdomain}.playcast.io` : `${branchName}.playcast.io`;
    return ensureTrailingSlash(`https://${domain}`);
  }
}

const generateDownloadUrl = (downloadUrlName: keyof typeof DOWNLOAD_URLS, branchName: string, version: string): string => {
  const downloadUrl = DOWNLOAD_URLS[downloadUrlName];
  const name = downloadUrl.nameTemplate.replace('${version}', version);
  const subdirectory = branchName === 'local' ? 'dev' : branchName === 'release' ? 'beta' : branchName;
  return `https://${downloadUrl.bucketName}.s3.amazonaws.com/${subdirectory}/${name}`;
}

// Load config for developer overrides (optional - file may not exist in production)
const loadConfigOverrides = (branchName: string): Partial<GlobalRoutes> => {
  try {
    if(branchName !== 'local') return {};

    // Extract only the URL properties that match GlobalRoutes
    const overrides: Partial<GlobalRoutes> = {};

    console.warn('[GlobalConfig] Loading config overrides for branch:', branchName);

    // Infrastructure service overrides
    if (config.API_BASE_URL) overrides.API_BASE_URL = config.API_BASE_URL;
    if (config.METRICS_API_BASE_URL) overrides.METRICS_API_BASE_URL = config.METRICS_API_BASE_URL;
    if (config.REALTIME_API_BASE_URL) overrides.REALTIME_API_BASE_URL = config.REALTIME_API_BASE_URL;
    if (config.REALTIME_API_WEBSOCKET_URL) overrides.REALTIME_API_WEBSOCKET_URL = config.REALTIME_API_WEBSOCKET_URL;
    if (config.ANALYTICS_API_URL) overrides.ANALYTICS_API_URL = config.ANALYTICS_API_URL

    // Website overrides
    if (config.PAVILION_PLAYER_BASE_URL) overrides.PAVILION_PLAYER_BASE_URL = config.PAVILION_PLAYER_BASE_URL;
    if (config.PRIME_PLAYER_BASE_URL) overrides.PRIME_PLAYER_BASE_URL = config.PRIME_PLAYER_BASE_URL;
    if (config.DASHBOARD_BASE_URL) overrides.DASHBOARD_BASE_URL = config.DASHBOARD_BASE_URL;
    if (config.MARKETING_BASE_URL) overrides.MARKETING_BASE_URL = config.MARKETING_BASE_URL;
    if (config.DEMO_BASE_URL) overrides.DEMO_BASE_URL = config.DEMO_BASE_URL;
    if (config.BEACON_BASE_URL) overrides.BEACON_BASE_URL = config.BEACON_BASE_URL;
    if (config.ADMIN_DASHBOARD_BASE_URL) overrides.ADMIN_DASHBOARD_BASE_URL = config.ADMIN_DASHBOARD_BASE_URL;

    console.warn('[GlobalConfig] Config overrides loaded:', overrides);

    return overrides;
  } catch {
    // Config file doesn't exist or couldn't be loaded - this is fine for production
    return {};
  }
}

export const generateRoutes = (branchName: string, version: string): GlobalRoutes => {
  console.warn('[GlobalConfig] Generating routes for branch:', branchName);
  // Generate infrastructure service URLs (keep existing gibber.com/pogcast.lol logic)
  const baseRoutes: GlobalRoutes = {
    // Infrastructure Services
    API_BASE_URL: generateInfraServiceUrl('API', branchName),
    METRICS_API_BASE_URL: generateInfraServiceUrl('METRICS_API', branchName),
    REALTIME_API_BASE_URL: generateInfraServiceUrl('REALTIME_API', branchName),
    REALTIME_API_WEBSOCKET_URL: generateInfraServiceUrl('REALTIME_API_WEBSOCKET', branchName),
    ANALYTICS_API_URL: generateAnalyticsUrl('PLAYCAST_ANALYTICS', branchName),

    // Websites (always use playcast.io)
    PAVILION_PLAYER_BASE_URL: generateWebsiteUrl('PAVILION_PLAYER', branchName),
    PRIME_PLAYER_BASE_URL: generateWebsiteUrl('PRIME_PLAYER', branchName),
    MARKETING_BASE_URL: generateWebsiteUrl('MARKETING', branchName),
    DEMO_BASE_URL: generateWebsiteUrl('DEMO', branchName),
    BEACON_BASE_URL: generateWebsiteUrl('BEACON', branchName),
    DASHBOARD_BASE_URL: generateWebsiteUrl('DASHBOARD', branchName),
    ADMIN_DASHBOARD_BASE_URL: generateWebsiteUrl('ADMIN_DASHBOARD', branchName),

    // Download URLs
    PLAYCAST_DOWNLOAD_URL: generateDownloadUrl('PLAYCAST_DOWNLOAD_URL', branchName, version),
    PAVILION_DOWNLOAD_URL: generateDownloadUrl('PAVILION_DOWNLOAD_URL', branchName, version),
  };

  // Apply developer overrides from config.json
  const overrides = loadConfigOverrides(branchName);

  return {
    ...baseRoutes,
    ...overrides, // Developer overrides take precedence
  };
}
